import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import SettingsTab from "@/pages/admin/tabs/SettingsTab";
import PagesTab from "@/pages/admin/tabs/PagesTab";
import FeaturesTab from "@/pages/admin/tabs/FeaturesTab";
import MessagesTab from "@/pages/admin/tabs/MessagesTab";
import DeletionsTab from "@/pages/admin/tabs/DeletionsTab";
import { Settings, FileText, LayoutGrid, Mail, UserX, LogOut, Mountain, Crown } from "lucide-react";

const TABS = [
  { id: "settings", label: "Site Ayarları", icon: Settings },
  { id: "pages", label: "Sayfa Yönetimi", icon: FileText },
  { id: "features", label: "Özellik Kartları", icon: LayoutGrid },
  { id: "premium", label: "Premium Kartları", icon: Crown },
  { id: "messages", label: "İletişim Talepleri", icon: Mail },
  { id: "deletions", label: "Hesap Silme Talepleri", icon: UserX },
];

export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const { reloadSettings } = useSite();
  const [tab, setTab] = useState("settings");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (admin) api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [admin, tab]);

  if (!ready) return <div className="min-h-screen grid place-items-center bg-[#F8F9FA] text-[#5C6F62]">Yükleniyor...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-72 md:min-h-screen bg-[#0A1A10] text-white p-5 md:p-6 flex md:flex-col md:sticky md:top-0 shrink-0">
        <div className="flex items-center gap-2.5 mb-0 md:mb-8">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#1B4D2E] border border-[#D4AF37]/40">
            <Mountain className="h-5 w-5 text-[#D4AF37]" />
          </span>
          <span className="font-serif-display text-lg font-bold hidden md:block">Saklı Rota CMS</span>
        </div>
        <nav className="hidden md:flex flex-col gap-1 flex-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left ${
                tab === t.id ? "bg-[#1B4D2E] text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "messages" && stats?.contacts_new ? <span className="ml-auto text-[10px] bg-[#D4AF37] text-[#0F2F1D] rounded-full px-1.5 font-bold">{stats.contacts_new}</span> : null}
              {t.id === "deletions" && stats?.deletions_pending ? <span className="ml-auto text-[10px] bg-[#D4AF37] text-[#0F2F1D] rounded-full px-1.5 font-bold">{stats.deletions_pending}</span> : null}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout-btn" className="ml-auto md:ml-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors">
          <LogOut className="h-4 w-4" /> <span className="hidden md:inline">Çıkış Yap</span>
        </button>
      </aside>

      {/* Mobile tab selector */}
      <div className="md:hidden bg-[#0A1A10] px-5 pb-4">
        <select value={tab} onChange={(e) => setTab(e.target.value)} data-testid="admin-mobile-tab" className="w-full sr-input !bg-[#1B4D2E] !text-white !border-white/20">
          {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      {/* Content */}
      <main className="flex-1 p-5 sm:p-8 lg:p-10 max-w-5xl">
        <header className="mb-8">
          <h1 className="font-serif-display text-3xl font-bold text-[#0F2F1D]" data-testid="admin-page-title">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
          {stats && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Toplam Mesaj" value={stats.contacts} />
              <Stat label="Yeni Mesaj" value={stats.contacts_new} accent />
              <Stat label="Silme Talebi" value={stats.deletions} />
              <Stat label="Bekleyen Talep" value={stats.deletions_pending} accent />
            </div>
          )}
        </header>

        {tab === "settings" && <SettingsTab onSaved={reloadSettings} />}
        {tab === "pages" && <PagesTab />}
        {tab === "features" && <FeaturesTab endpoint="features" title="Ana Sayfa Özellik Kartları" />}
        {tab === "premium" && <FeaturesTab endpoint="premium-features" title="Premium Özellik Kartları" />}
        {tab === "messages" && <MessagesTab />}
        {tab === "deletions" && <DeletionsTab />}
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-[#0F2F1D] border-[#0F2F1D] text-white" : "bg-white border-[#E2E8E4]"}`}>
      <p className={`font-mono-accent text-[11px] uppercase tracking-wider ${accent ? "text-[#D4AF37]" : "text-[#5C6F62]"}`}>{label}</p>
      <p className="text-3xl font-bold mt-1 font-serif-display">{value}</p>
    </div>
  );
}
